import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { OrganizationProvider } from './contexts/OrganizationContext';
import Layout from './components/Layout';
import StagingBanner from './components/StagingBanner';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import UnsubscribePage from './pages/UnsubscribePage';
import OrganizationSelector from './pages/OrganizationSelector';
import FeedPage from './pages/FeedPage';
import PaymentsPage from './pages/PaymentsPage';
import EkonomiPage from './pages/EkonomiPage';
import DocumentsPage from './pages/DocumentsPage';
import ProposalsPage from './pages/ProposalsPage';
import MembersPage from './pages/MembersPage';
import SettingsPage from './pages/SettingsPage';
import BillingPage from './pages/BillingPage';
import SupportPage from './pages/SupportPage';
import DemoRoot from './demo/DemoRoot';
import { trackPageView } from './services/AnalyticsService';

// Tracks the route pattern rather than the raw path so pages group across orgs.
function AnalyticsTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    trackPageView(pathname.replace(/^\/org\/[^/]+/, '/org/:orgId'));
  }, [pathname]);
  return null;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (user) return <Navigate to="/organizations" replace />;
  return <>{children}</>;
}

const orgChildRoutes = (
  <>
    <Route path="feed" element={<FeedPage />} />
    <Route path="payments" element={<PaymentsPage />} />
    <Route path="ekonomi" element={<EkonomiPage />} />
    <Route path="documents" element={<DocumentsPage />} />
    <Route path="proposals" element={<ProposalsPage />} />
    <Route path="members" element={<MembersPage />} />
    <Route path="billing" element={<BillingPage />} />
    <Route path="settings" element={<SettingsPage />} />
    <Route path="support" element={<SupportPage />} />
    <Route index element={<Navigate to="feed" replace />} />
  </>
);

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/unsubscribe" element={<UnsubscribePage />} />
      <Route path="/organizations" element={<ProtectedRoute><OrganizationSelector /></ProtectedRoute>} />
      <Route path="/org/:orgId" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        {orgChildRoutes}
      </Route>
      <Route path="*" element={<Navigate to="/organizations" replace />} />
    </Routes>
  );
}

// The demo lives outside the real providers so none of their requests can be answered by the demo backend.
export default function App() {
  return (
    <BrowserRouter>
      <StagingBanner />
      <AnalyticsTracker />
      <Routes>
        <Route path="/demo" element={<Navigate to="/org/demo/feed" replace />} />
        <Route path="/org/demo" element={<DemoRoot />}>
          {orgChildRoutes}
        </Route>
        <Route path="*" element={
          <AuthProvider>
            <OrganizationProvider>
              <AppRoutes />
            </OrganizationProvider>
          </AuthProvider>
        } />
      </Routes>
    </BrowserRouter>
  );
}
